import { AccountStatus } from "@components/AccountStatus"
import { PersonIdentity } from "@components/PersonIdentity"
import { RowActions, type RowAction } from "@components/ui/RowActions"
import { Table, type TableColumn } from "@components/ui/Table"
import { KeyRound, Link2, Mail, RotateCcw, Trash2 } from "lucide-react"
import { memo } from "react"
import { useTranslation } from "react-i18next"

import type { Professor } from "../types"

import { ROUTES } from "@/constants/routes"

interface Props {
  professors: Professor[]
  caption: string
  /** Show where each professor teaches (the list spans several schools). */
  showSchool: boolean
  stale?: boolean
  sort?: { key: string; desc: boolean }
  onSort?: (key: string) => void
  canManage: boolean
  /**
   * Superusers only: mint and copy the activation link of a pending account
   * (PRD-33 §FR-13) or the reset link of an active one (PRD-34 FR-22).
   */
  canCopyInviteLink: boolean
  busyId: string | null
  onReinvite: (professor: Professor) => void
  onCopyInviteLink: (professor: Professor) => void
  onCopyResetLink: (professor: Professor) => void
  onTrash: (professor: Professor) => void
  onRestore: (professor: Professor) => void
}

const displayName = (p: Professor) => p.full_name || p.name

export const ProfessorsTable = memo(function ProfessorsTable({
  professors,
  caption,
  showSchool,
  stale,
  sort,
  onSort,
  canManage,
  canCopyInviteLink,
  busyId,
  onReinvite,
  onCopyInviteLink,
  onCopyResetLink,
  onTrash,
  onRestore,
}: Props) {
  const { t } = useTranslation("professors")

  const columns: TableColumn<Professor>[] = [
    ...(showSchool
      ? [
          {
            key: "school",
            header: t("fields.school"),
            sortKey: "school",
            cell: (p: Professor) => p.school_name,
          },
        ]
      : []),
    // "Ciclo escolar" left the list: every row read "2025-2026", and a column
    // decided per page would come and go between pages. It is on the page.
    {
      key: "status",
      header: t("fields.status"),
      sortKey: "status",
      cell: (p) => (
        <AccountStatus
          status={p.invite_status}
          invitedAt={p.invited_at}
          acceptedAt={p.invite_accepted_at}
          expiresAt={p.invite_expires_at}
          deletedAt={p.deleted_at}
        />
      ),
    },
  ]

  // Editing lives on the professor's page (PRD-56 PR (4)); the row keeps
  // only what its state asks for.
  function actionsFor(p: Professor) {
    const trash: RowAction = {
      label: t("actions.trash"),
      icon: <Trash2 className="h-4 w-4" />,
      onClick: () => onTrash(p),
      danger: true,
    }
    if (p.deleted_at) {
      return (
        <RowActions
          subject={displayName(p)}
          primary={{
            label: t("actions.restore"),
            icon: <RotateCcw className="h-4 w-4" />,
            onClick: () => onRestore(p),
          }}
        />
      )
    }
    if (p.invite_status === "accepted") {
      return (
        <RowActions
          subject={displayName(p)}
          items={[
            ...(canCopyInviteLink
              ? [
                  {
                    label: t("actions.copy_reset_link"),
                    icon: <KeyRound className="h-4 w-4" />,
                    onClick: () => onCopyResetLink(p),
                  },
                ]
              : []),
            trash,
          ]}
        />
      )
    }
    // Not active yet: the row's own question is "did the invitation arrive?".
    return (
      <RowActions
        subject={displayName(p)}
        primary={{
          label: p.invite_status === "not_invited" ? t("actions.invite") : t("actions.reinvite"),
          icon: <Mail className="h-4 w-4" />,
          onClick: () => onReinvite(p),
          loading: busyId === p.id,
        }}
        items={[
          ...(canCopyInviteLink
            ? [
                {
                  label: t("actions.copy_link"),
                  icon: <Link2 className="h-4 w-4" />,
                  onClick: () => onCopyInviteLink(p),
                },
              ]
            : []),
          trash,
        ]}
      />
    )
  }

  return (
    <Table
      caption={caption}
      rows={professors}
      rowKey={(p) => p.id}
      identity={{
        header: t("fields.teacher"),
        sortKey: "name",
        cell: (p) => (
          <PersonIdentity name={displayName(p)} email={p.email} to={ROUTES.professorFor(p.id)} />
        ),
      }}
      columns={columns}
      actions={canManage ? actionsFor : undefined}
      actionsHeader={t("fields.actions")}
      stale={stale}
      cards="inline"
      sort={sort}
      onSort={onSort}
    />
  )
})
